import {useMediaQuery} from '@mui/material'
import moment from 'moment'
import React, {forwardRef, ForwardRefRenderFunction, useEffect, useMemo, useRef} from 'react'
import {useDispatch} from 'react-redux'
import {useParams} from 'react-router'
import {useNavigate} from 'react-router-dom'
import {
  DialogItemType,
  GetDialogs,
  useDialogsCursor,
  useDialogsList,
  useDialogsSearch,
  useDialogsSearchCursor,
  useDialogsSearchList,
  GetDialogsSearch,
  useMockDialog,
  setMockDialog,
  setOpenDialog,
  ContentLastMessage,
  clearMessagesState
} from 'entities/messages'
import {NoResults} from 'features/noResults'
import {useVirtualScroll} from 'shared/hooks'
import {mediaVariants} from 'shared/lib'
import {displayCount} from 'shared/lib/entity'
import {Avatar} from 'shared/ui/avatar'
import {PinActive} from 'shared/ui/icons'
import {Loader} from 'shared/ui/loader'
import {SearchDialogsMobile} from '../searchDialogsMobile'
import styles from './styles.module.scss'

const DialogsForwardRef: ForwardRefRenderFunction<HTMLDivElement> = (_, forwardRef) => {
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const {id: idUser} = useParams()
  const is768p = useMediaQuery(`(max-width: ${styles.is768p})`)
  const ref = useRef<HTMLDivElement | null>(null)
  const search = useDialogsSearch()
  const cursor = useDialogsCursor()
  const cursorSearch = useDialogsSearchCursor()
  const dialogsList = useDialogsList()
  const dialogsSearchList = useDialogsSearchList()
  const mockDialog = useMockDialog()

  const {refetch: loadDialogs, isError: isErrorDialogs, isFetching: isFetchingDialogs} = GetDialogs(dispatch)
  const {refetch: loadSearch, isError: isErrorSearch, isFetching: isFetchingSearch} = GetDialogsSearch(dispatch)

  const list = useMemo(() => search ? dialogsSearchList : dialogsList, [search, dialogsSearchList, dialogsList])
  const isFetching = useMemo(() => search ? isFetchingSearch : isFetchingDialogs, [search, isFetchingSearch, isFetchingDialogs])
  const isError = useMemo(() => search ? isErrorSearch : isErrorDialogs, [search, isErrorSearch, isErrorDialogs])

  useEffect(() => {
    if (is768p || idUser || !list.length) return

    dispatch(setOpenDialog(list[0]))
    navigate(`/messages/${list[0].interlocutor.id}`)
  }, [is768p, idUser, list, navigate])

  useEffect(() => {
    if (!mockDialog?.user) return

    if (dialogsList.find((item) => item.interlocutor.id === mockDialog.user?.id)) {
      dispatch(setMockDialog(undefined))
      dispatch(clearMessagesState())
    }
  }, [dialogsList, mockDialog])

  useVirtualScroll({ref, isError: isErrorDialogs, isFetching: isFetchingDialogs, load: loadDialogs, cursor})
  useVirtualScroll({ref, isError: isErrorSearch, isFetching: isFetchingSearch, load: loadSearch, cursor: cursorSearch, skip: !search})

  const setRef = (node: HTMLDivElement | null) => {
    ref.current = node

    if (typeof forwardRef === 'function') {
      forwardRef(node)
    } else if (forwardRef) {
      forwardRef.current = node
    }
  }

  return (
    <div className={`${styles.dialogs} ${styles.customScroll}`} ref={setRef}>
      {is768p ? <SearchDialogsMobile /> : null}
      {mockDialog && !search ? <MockDialog /> : null}
      {list.map((item) => <DialogItem key={item.interlocutor.id} {...item} />)}
      {!(list.length || isFetching || (mockDialog && !search))
        ? <NoResults header='No dialogues' text="You haven't had any dialogues yet." />
        : null}
      {isFetching ? <div className={styles.loader}><Loader /></div> : null}
      {isError ? <div>Error</div> : null}
    </div>
  )
}

export const Dialogs = forwardRef(DialogsForwardRef)

const DialogItem: React.FC<DialogItemType> = (dialog) => {
  const {interlocutor, unreadCount, lastMessage, isPinned, idLastReadMessage} = dialog
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const {id} = useParams()
  const isActive = interlocutor.id === id
  const is768p = useMediaQuery(`(max-width: ${styles.is768p})`)
  const dateCreatedFormat = useMemo(() => {
    const date = moment(lastMessage.dateCreated)

    if (date.isSame(moment(), 'day')) return date.format('HH:mm')
    if (date.isSame(moment().subtract(1, 'days'), 'day')) return 'Yesterday'
    for (let i = 2; i <= 7; i++) {
      if (date.isSame(moment().subtract(i, 'days'), 'day')) return `${i} days ago`
    }
    if (date.isSame(moment(), 'year')) return date.format('DD MMM')

    return date.format('DD MMM YYYY')
  }, [lastMessage.dateCreated])

  const clickHandler = () => {
    dispatch(setOpenDialog({interlocutor, unreadCount, lastMessage, isPinned, idLastReadMessage}))
    navigate(`/messages/${interlocutor.id}`)
  }

  return (
    <div className={`${styles.dialogItem} ${isActive ? styles.active : ''}`} onClick={clickHandler}>
      <Avatar
        src={interlocutor.avatarUrl + mediaVariants.square64}
        isOnline={interlocutor.isOnline}
        onlineClass={styles.online}
        sx={is768p ? {width: 52, height: 52} : {}}
      />
      <div className={styles.info}>
        <div className={styles.header}>
          <div className={styles.name}>
            {interlocutor.name}
            {isPinned ? <div className={styles.pinIcon}><PinActive /></div> : null}
          </div>
          <div className={styles.time}>{dateCreatedFormat}</div>
        </div>
        <div className={styles.content}>
          <div className={styles.lastMessage}>
            <ContentLastMessage dialog={dialog} />
          </div>
          {!isActive && unreadCount ? <div className={styles.unread}>{displayCount(unreadCount)}</div> : null}
        </div>
      </div>
    </div>
  )
}

const MockDialog = () => {
  const {id} = useParams()
  const mockDialog = useMockDialog()
  const navigate = useNavigate()
  const is768p = useMediaQuery(`(max-width: ${styles.is768p})`)
  const isActive = useMemo(() => mockDialog?.user?.id === id, [mockDialog?.user?.id, id])

  if (!mockDialog?.user) return <></>

  const clickHandler = () => {
    navigate(`/messages/${mockDialog.user?.id}`)
  }

  return (
    <div className={`${styles.dialogItem} ${isActive ? styles.active : ''}`} onClick={clickHandler}>
      <Avatar
        src={mockDialog.user?.avatarUrl + mediaVariants.square64}
        isOnline={mockDialog.user?.isOnline}
        onlineClass={styles.online}
        sx={is768p ? {width: 52, height: 52} : {}}
      />
      <div className={styles.info}>
        <div className={styles.header}>
          <div className={styles.name}>
            {mockDialog.user?.name}
          </div>
        </div>
        <div className={styles.content}>
          <div className={styles.lastMessage}>
            <div className={styles.chatRequestText}>
              {mockDialog.chatRequest?.content?.message}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}